import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { AgentConfig, KbArticulo, AgenteMemoria, TipoMemoria } from '@/lib/types'
import AgentConfigEditor from '@/components/agentes/AgentConfigEditor'
import SkillLibrary from '@/components/agentes/SkillLibrary'
import { PageHeader } from '@/components/ui'

/* ── Types ── */
interface AgentStats {
  totalConversations: number
  activeConversations: number
  totalMessages: number
  avgMessagesPerConv: number
  topLeadScore: { cold: number; warm: number; hot: number }
  handoffs: { whatsapp: number; email: number }
}

interface ConvRow {
  id: number
  estado: string
  metadata: Record<string, unknown> | null
  created_at: string
}

const WIDGET_SCRIPT = `<script src="https://1conecta-erp.pages.dev/chatbot.js" async></script>`

/* ── Icons ── */
const icons = {
  chat: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  fire: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 12c2-2.96 0-7-1-8 0 3.038-1.773 4.741-3 6-1.226 1.26-2 3.24-2 5a6 6 0 1012 0c0-1.532-1.056-3.94-2-5-1.786 3-2 2-4 2z" />
    </svg>
  ),
  whatsapp: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  email: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  copy: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  check: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  play: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  eye: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  sparkles: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"/>
    </svg>
  ),
  bot: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7v1H3v-1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2z" />
      <circle cx="8" cy="13" r="1" /><circle cx="16" cy="13" r="1" />
      <path d="M7 17h10a4 4 0 01-10 0z" />
    </svg>
  ),
  snowflake: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/>
      <line x1="5.64" y1="5.64" x2="18.36" y2="18.36"/><line x1="18.36" y1="5.64" x2="5.64" y2="18.36"/>
      <line x1="12" y1="2" x2="14" y2="5"/><line x1="12" y1="2" x2="10" y2="5"/>
      <line x1="12" y1="22" x2="14" y2="19"/><line x1="12" y1="22" x2="10" y2="19"/>
    </svg>
  ),
  thermometer: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/>
    </svg>
  ),
  brain: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.44 2.5 2.5 0 0 1 0-3.12 3 3 0 0 1 0-3.88 2.5 2.5 0 0 1 0-3.12A2.5 2.5 0 0 1 9.5 2z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.44 2.5 2.5 0 0 0 0-3.12 3 3 0 0 0 0-3.88 2.5 2.5 0 0 0 0-3.12A2.5 2.5 0 0 0 14.5 2z"/>
    </svg>
  ),
  book: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
    </svg>
  ),
  link: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  ),
  upload: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  fileText: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  paperclip: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
    </svg>
  ),
  warning: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  camera: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  edit: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  externalLink: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  ),
  users: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  refresh: (s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  ),
}

/* ── Agent avatar colors for initials fallback ── */
const AGENT_COLORS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
]

function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

function getAgentColor(id: number): string {
  return AGENT_COLORS[id % AGENT_COLORS.length]
}

interface AgentesProps {
  /**
   * Dentro de Configuración → Comunicaciones la página ya tiene encabezado propio, y
   * dos títulos uno encima del otro se leen como un error de maquetado.
   */
  embebido?: boolean
}

export default function Agentes({ embebido = false }: AgentesProps = {}) {
  const [agents, setAgents] = useState<AgentConfig[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null)
  const [stats, setStats] = useState<AgentStats | null>(null)
  const [loadingAgents, setLoadingAgents] = useState(true)
  const [loadingStats, setLoadingStats] = useState(true)
  const [showPreview, setShowPreview] = useState(false)
  const [copied, setCopied] = useState(false)

  const API_URL_AGENTES = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

  async function uploadAvatar(file: File) {
    if (!selectedAgentId) return
    if (file.size > 10 * 1024 * 1024) { alert('La imagen no debe superar 10 MB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Solo JPG, PNG o WebP'); return
    }
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('agente_id', String(selectedAgentId))
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL_AGENTES}/api/agentes/upload-avatar`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData,
      })
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Error') }
      alert('Avatar subido correctamente')
      loadAgents()
    } catch (err: any) {
      alert(err.message || 'Error al subir avatar')
    }
  }

  const selectedAgent = agents.find(a => a.id === selectedAgentId) ?? null

  /* ── Load all agents ── */
  const loadAgents = useCallback(async () => {
    setLoadingAgents(true)
    try {
      const { data } = await supabase
        .from('agente_config')
        .select('*')
        .order('id', { ascending: true })
      const list = (data ?? []) as unknown as AgentConfig[]
      setAgents(list)
      // Auto-select first agent if none selected
      if (list.length > 0 && !selectedAgentId) {
        setSelectedAgentId(list[0].id)
      }
    } catch (err) {
      console.error('Error loading agents:', err)
    }
    setLoadingAgents(false)
  }, [selectedAgentId])

  useEffect(() => { loadAgents() }, [loadAgents])

  /* ── Load stats (web_chat conversations) ── */
  const loadStats = useCallback(async () => {
    setLoadingStats(true)
    try {
      const { data: convs } = await supabase
        .from('conversaciones')
        .select('id, estado, metadata, created_at')
        .eq('canal', 'web_chat')

      const conversations = (convs ?? []) as unknown as ConvRow[]
      const active = conversations.filter((c: ConvRow) => c.estado === 'abierta')
      const convIds = conversations.map((c: ConvRow) => c.id)
      let totalMsgs = 0
      let handoffWa = 0
      let handoffEmail = 0
      const leadScores = { cold: 0, warm: 0, hot: 0 }

      if (convIds.length > 0) {
        const { count } = await supabase
          .from('mensajes')
          .select('id', { count: 'exact', head: true })
          .in('conversacion_id', convIds)
        totalMsgs = count ?? 0
      }

      for (const conv of conversations) {
        const meta = conv.metadata as Record<string, unknown> | null
        if (meta) {
          const score = meta.lead_score as string
          if (score === 'cold') leadScores.cold++
          else if (score === 'warm') leadScores.warm++
          else if (score === 'hot') leadScores.hot++
          if (meta.handoff_channel === 'whatsapp') handoffWa++
          if (meta.handoff_channel === 'email') handoffEmail++
        }
      }

      setStats({
        totalConversations: conversations.length,
        activeConversations: active.length,
        totalMessages: totalMsgs,
        avgMessagesPerConv: conversations.length > 0
          ? Math.round((totalMsgs / conversations.length) * 10) / 10 : 0,
        topLeadScore: leadScores,
        handoffs: { whatsapp: handoffWa, email: handoffEmail },
      })
    } catch (err) {
      console.error('Error loading agent stats:', err)
    }
    setLoadingStats(false)
  }, [])

  useEffect(() => { loadStats() }, [loadStats])

  // Cleanup: remove preview widget when leaving the page or switching agents
  useEffect(() => {
    return () => {
      const script = document.getElementById('chatbot-preview-script')
      if (script) script.remove()
      const container = document.getElementById('oneconecta-chatbot')
      if (container) container.remove()
    }
  }, [])

  const handleCopy = () => {
    navigator.clipboard.writeText(WIDGET_SCRIPT)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const togglePreview = () => {
    setShowPreview(prev => {
      const next = !prev
      if (next) {
        // Remove any stale widget first
        const oldScript = document.getElementById('chatbot-preview-script')
        if (oldScript) oldScript.remove()
        const oldContainer = document.getElementById('oneconecta-chatbot')
        if (oldContainer) oldContainer.remove()

        // Load fresh chatbot.js with cache-busting
        const script = document.createElement('script')
        script.id = 'chatbot-preview-script'
        script.src = `/chatbot.js?v=${Date.now()}`
        script.async = true
        document.body.appendChild(script)
      } else {
        const script = document.getElementById('chatbot-preview-script')
        if (script) script.remove()
        const container = document.getElementById('oneconecta-chatbot')
        if (container) container.remove()
      }
      return next
    })
  }

  /** Reset preview: clear all session data and reload the widget fresh */
  const resetPreview = () => {
    // 1. Remove the current widget
    const script = document.getElementById('chatbot-preview-script')
    if (script) script.remove()
    const container = document.getElementById('oneconecta-chatbot')
    if (container) container.remove()

    // 2. Clear all chatbot session data
    const keysToRemove = [
      '1conecta_chatbot_session',
      '1conecta_chatbot_name',
      '1conecta_visit_count',
      '1conecta_proactive_cooldown',
      '1conecta_proactive_shown',
      '1conecta_last_visit',
    ]
    keysToRemove.forEach(k => {
      try { localStorage.removeItem(k) } catch { /* */ }
      try { sessionStorage.removeItem(k) } catch { /* */ }
    })
    // Clear the persistent cookie
    document.cookie = '1c_vid=;max-age=0;path=/'

    // 3. Re-inject the widget after a brief delay
    setTimeout(() => {
      const newScript = document.createElement('script')
      newScript.id = 'chatbot-preview-script'
      newScript.src = `/chatbot.js?v=${Date.now()}`
      newScript.async = true
      document.body.appendChild(newScript)
    }, 300)
  }

  const isValentina = selectedAgent?.slug === 'valentina'

  return (
    <div className={embebido ? undefined : 'page-enter'}>
      {!embebido && (
        <PageHeader breadcrumbs={[{ label: 'Configuración' }, { label: 'Agentes IA' }]} />
      )}

      {/* ── Agent Selector Grid ── */}
      <div className="agente-selector-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 14,
        marginBottom: 24,
      }}>
        {loadingAgents ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)', gridColumn: '1 / -1' }}>
            Cargando agentes...
          </div>
        ) : agents.map(agent => {
          const isSelected = agent.id === selectedAgentId
          return (
            <button
              key={agent.id}
              onClick={() => { setSelectedAgentId(agent.id) }}
              className="agente-card-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
                background: isSelected ? 'var(--bg-surface)' : 'var(--bg-card)',
                border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--border-color)',
                borderRadius: 14, cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.2s ease',
                boxShadow: isSelected ? '0 0 0 3px color-mix(in srgb, var(--brand-500) 12%, transparent)' : 'none',
              }}
            >
              {/* Avatar */}
              {agent.avatar_url ? (
                <img
                  src={agent.avatar_url}
                  alt={agent.nombre}
                  style={{
                    width: 48, height: 48, borderRadius: '50%', objectFit: 'cover',
                    border: isSelected ? '3px solid var(--color-primary)' : '2px solid var(--border-color)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                  background: getAgentColor(agent.id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 700, color: 'var(--text-on-primary)',
                  border: isSelected ? '3px solid var(--color-primary)' : '2px solid transparent',
                }}>
                  {getInitials(agent.nombre)}
                </div>
              )}

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {agent.nombre}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                    background: agent.activo ? 'color-mix(in srgb, var(--success) 10%, transparent)' : 'rgba(239, 68, 68, 0.1)',
                    color: agent.activo ? 'var(--success)' : 'var(--danger)',
                    border: `1px solid ${agent.activo ? 'color-mix(in srgb, var(--success) 20%, transparent)' : 'rgba(239, 68, 68, 0.2)'}`,
                  }}>
                    {agent.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                  {agent.rol} &middot; {agent.modelo}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* ── Selected Agent Detail ── */}
      {selectedAgent && (
        <>
          {/* ── Agent Profile Card ── */}
          <div className="card" style={{ marginBottom: 24, overflow: 'visible' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {selectedAgent.avatar_url ? (
                  <img
                    src={selectedAgent.avatar_url}
                    alt={selectedAgent.nombre}
                    style={{
                      width: 52, height: 52, borderRadius: '50%', objectFit: 'cover',
                      border: '3px solid var(--color-primary)',
                      boxShadow: '0 0 0 3px color-mix(in srgb, var(--brand-500) 12%, transparent)',
                    }}
                  />
                ) : (
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: getAgentColor(selectedAgent.id),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700, color: 'var(--text-on-primary)',
                    border: '3px solid var(--color-primary)',
                    boxShadow: '0 0 0 3px color-mix(in srgb, var(--brand-500) 12%, transparent)',
                  }}>
                    {getInitials(selectedAgent.nombre)}
                  </div>
                )}
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedAgent.nombre}
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                      background: selectedAgent.activo ? 'color-mix(in srgb, var(--success) 10%, transparent)' : 'rgba(239, 68, 68, 0.1)',
                      color: selectedAgent.activo ? 'var(--success)' : 'var(--danger)',
                      border: `1px solid ${selectedAgent.activo ? 'color-mix(in srgb, var(--success) 20%, transparent)' : 'rgba(239, 68, 68, 0.2)'}`,
                    }}>
                      {selectedAgent.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </h2>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {selectedAgent.rol} &middot; {selectedAgent.modelo}
                  </span>
                </div>
              </div>
              {isValentina && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className={`btn ${showPreview ? 'btn-danger' : 'btn-primary'}`} onClick={togglePreview} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {showPreview ? icons.eye(16) : icons.play(16)}
                    {showPreview ? 'Ocultar Preview' : 'Preview en Vivo'}
                  </button>
                  {showPreview && (
                    <button className="btn btn-secondary" onClick={resetPreview} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {icons.refresh(16)} Resetear Conversación
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Agent Avatar ── */}
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-color)',
              borderRadius: 12, padding: 16, marginBottom: 20,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                {icons.camera(16)} Avatar del Agente
              </h3>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <img
                  src={selectedAgent.avatar_url || (isValentina ? '/assets/brand/valentina-main.svg' : '/assets/brand/marco-avatar.svg')}
                  alt={selectedAgent.nombre}
                  style={{
                    width: 120, height: 120, borderRadius: 16, objectFit: 'cover',
                    border: '2px solid var(--border-color)',
                    boxShadow: '0 4px 20px rgba(var(--shadow-rgb), calc(.08 * var(--shadow-k)))',
                  }}
                  onError={(e) => { (e.target as HTMLImageElement).src = isValentina ? '/assets/brand/valentina-main.svg' : '/assets/brand/marco-avatar.svg' }}
                />
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '10px 18px', borderRadius: 8,
                      border: '2px dashed var(--border-color)', cursor: 'pointer',
                      color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500,
                    }}
                    onDragOver={(e) => { e.preventDefault() }}
                    onDrop={async (e) => {
                      e.preventDefault()
                      const file = e.dataTransfer.files[0]
                      if (file) await uploadAvatar(file)
                    }}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (file) await uploadAvatar(file)
                      }}
                    />
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Subir foto
                  </label>
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    Formatos: JPG, PNG o WebP. Maximo 10 MB.<br/>
                    Se generan 3 tamanos optimizados automaticamente.
                  </div>
                </div>
              </div>
            </div>

            {/* ── Stats Grid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
              <div className="stat-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{icons.chat(16)} Conversaciones</div>
                <div className="stat-value">{loadingStats ? '--' : stats?.totalConversations ?? 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{icons.bot(16)} Activas</div>
                <div className="stat-value success">{loadingStats ? '--' : stats?.activeConversations ?? 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                <div className="stat-label">Mensajes Totales</div>
                <div className="stat-value info">{loadingStats ? '--' : stats?.totalMessages ?? 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                <div className="stat-label">Promedio/Conv.</div>
                <div className="stat-value">{loadingStats ? '--' : stats?.avgMessagesPerConv ?? 0}</div>
              </div>
            </div>

            {/* ── Lead Temperature + Handoffs ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {icons.fire(16)} Temperatura de Leads
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Frio', key: 'cold' as const, color: 'var(--info)', icon: icons.snowflake(14) },
                    { label: 'Tibio', key: 'warm' as const, color: 'var(--warning)', icon: icons.thermometer(14) },
                    { label: 'Caliente', key: 'hot' as const, color: 'var(--danger)', icon: icons.fire(14) },
                  ].map(item => {
                    const count = stats?.topLeadScore[item.key] ?? 0
                    const total = (stats?.topLeadScore.cold ?? 0) + (stats?.topLeadScore.warm ?? 0) + (stats?.topLeadScore.hot ?? 0)
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0
                    return (
                      <div key={item.key}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{item.icon} {item.label}</span>
                          <span style={{ fontWeight: 600 }}>{loadingStats ? '--' : `${count} (${pct}%)`}</span>
                        </div>
                        <div style={{ height: 6, background: 'var(--bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', background: item.color,
                            borderRadius: 3, transition: 'width 0.6s ease',
                          }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>{icons.externalLink(16)} Conversiones (Handoffs)</h3>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8 }}>
                  <div style={{ flex: 1, textAlign: 'center', padding: '12px 0', background: 'rgba(37, 211, 102, 0.08)', borderRadius: 10, border: '1px solid rgba(37, 211, 102, 0.2)' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#25D366' }}>{loadingStats ? '--' : stats?.handoffs.whatsapp ?? 0}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                      {icons.whatsapp(14)} WhatsApp
                    </div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '12px 0', background: 'color-mix(in srgb, var(--primary-light) 8%, transparent)', borderRadius: 10, border: '1px solid color-mix(in srgb, var(--primary-light) 20%, transparent)' }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{loadingStats ? '--' : stats?.handoffs.email ?? 0}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                      {icons.email(14)} Email
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Embed Code (Valentina only) ── */}
            {isValentina && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>{icons.link(16)} Codigo de Instalacion</h3>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Agrega esta linea antes del cierre <code>&lt;/body&gt;</code> en cualquier pagina web:
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--bg-tertiary)', borderRadius: 8, padding: '10px 14px',
                  fontFamily: 'monospace', fontSize: 13, border: '1px solid var(--border-color)',
                }}>
                  <code style={{ flex: 1, overflow: 'auto', whiteSpace: 'nowrap' }}>{WIDGET_SCRIPT}</code>
                  <button
                    className="btn btn-secondary"
                    onClick={handleCopy}
                    style={{ padding: '6px 12px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                  >
                    {copied ? icons.check(14) : icons.copy(14)}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ══════════════════════════════════════════════
              Agent Configuration Editor
              ══════════════════════════════════════════════ */}
          <AgentConfigEditor agentId={selectedAgent.id} key={selectedAgent.id} />

          {/* ══════════════════════════════════════════════
              Skills Library (read-only)
              ══════════════════════════════════════════════ */}
          <SkillLibrary agentId={selectedAgent.id} agentName={selectedAgent.nombre} />

          {/* ══════════════════════════════════════════════
              Knowledge Base Section (filtered by agent)
              ══════════════════════════════════════════════ */}
          <KnowledgeBaseSection agentId={selectedAgent.id} agentName={selectedAgent.nombre} key={`kb-${selectedAgent.id}`} />

          {/* ══════════════════════════════════════════════
              Agent Memory Section (agente_memoria)
              ══════════════════════════════════════════════ */}
          <AgentMemorySection agentId={selectedAgent.id} agentName={selectedAgent.nombre} key={`mem-${selectedAgent.id}`} />
        </>
      )}
    </div>
  )
}

/* ── Category Labels ── */
const CATEGORY_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  pagina_web: { label: 'Página Web', color: 'var(--info)', icon: '🌐' },
  servicios: { label: 'Servicios', color: 'var(--primary-light)', icon: '⚙️' },
  metodologia: { label: 'Metodología', color: 'var(--primary-light)', icon: '📋' },
  casos_exito: { label: 'Casos de Éxito', color: 'var(--success)', icon: '🏆' },
  faq: { label: 'FAQ', color: 'var(--warning)', icon: '❓' },
  empresa: { label: 'Empresa', color: 'var(--danger)', icon: '🏢' },
  precios: { label: 'Precios', color: 'var(--info)', icon: '💰' },
  general: { label: 'General', color: 'var(--text-muted)', icon: '📄' },
}

/* KbArticle type imported from @/lib/types as KbArticulo */
type KbArticle = KbArticulo

interface KnowledgeBaseSectionProps {
  agentId: number
  agentName: string
}

function KnowledgeBaseSection({ agentId, agentName }: KnowledgeBaseSectionProps) {
  const [articles, setArticles] = useState<KbArticle[]>([])
  const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set())
  const [embeddedIds, setEmbeddedIds] = useState<Set<number>>(new Set())
  const [loadingKb, setLoadingKb] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingArticle, setEditingArticle] = useState<KbArticle | null>(null)
  const [form, setForm] = useState({ titulo: '', categoria: 'general', contenido_md: '', resumen: '', tags: '' })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [embedding, setEmbedding] = useState(false)
  const [kbViewTab, setKbViewTab] = useState<'assigned' | 'unassigned'>('assigned')
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  const loadArticles = useCallback(async () => {
    setLoadingKb(true)

    // Load all KB articles
    const { data: allArticles } = await supabase
      .from('kb_articulos')
      .select('*')
      .order('orden', { ascending: true })
      .order('created_at', { ascending: false })

    // Load agent-specific assignments
    const { data: assignments } = await supabase
      .from('agente_kb_articulos')
      .select('articulo_id')
      .eq('agente_id', agentId)

    // Check which articles have embeddings (embedding IS NOT NULL)
    const { data: embedded } = await supabase
      .from('kb_articulos')
      .select('id')
      .not('embedding', 'is', null)

    setArticles((allArticles ?? []) as unknown as KbArticle[])
    setAssignedIds(new Set((assignments ?? []).map((a: { articulo_id: number }) => a.articulo_id)))
    setEmbeddedIds(new Set((embedded ?? []).map((e: { id: number }) => e.id)))
    setLoadingKb(false)
  }, [agentId])

  useEffect(() => { loadArticles() }, [loadArticles])

  const openNew = () => {
    setEditingArticle(null)
    setForm({ titulo: '', categoria: 'general', contenido_md: '', resumen: '', tags: '' })
    setUploadFile(null)
    setShowModal(true)
  }

  const openEdit = (a: KbArticle) => {
    setEditingArticle(a)
    setForm({
      titulo: a.titulo,
      categoria: a.categoria,
      contenido_md: a.contenido_md,
      resumen: a.resumen || '',
      tags: a.tags.join(', '),
    })
    setUploadFile(null)
    setShowModal(true)
  }

  const handleToggle = async (a: KbArticle) => {
    await (supabase.from('kb_articulos') as any).update({ activo: !a.activo, updated_at: new Date().toISOString() }).eq('id', a.id)
    loadArticles()
  }

  const handleDelete = async (a: KbArticle) => {
    if (!confirm(`Eliminar "${a.titulo}"?`)) return
    await supabase.from('kb_articulos').delete().eq('id', a.id)
    loadArticles()
  }

  /* ── Assign / unassign article to this agent ── */
  const toggleAssignment = async (articleId: number) => {
    setTogglingId(articleId)
    try {
      if (assignedIds.has(articleId)) {
        // Unassign
        await supabase.from('agente_kb_articulos').delete()
          .eq('agente_id', agentId)
          .eq('articulo_id', articleId)
        setAssignedIds(prev => {
          const next = new Set(prev)
          next.delete(articleId)
          return next
        })
      } else {
        // Assign
        await (supabase.from('agente_kb_articulos') as any).insert({
          agente_id: agentId,
          articulo_id: articleId,
        })
        setAssignedIds(prev => new Set(prev).add(articleId))
      }
    } catch (err) {
      console.error('Error toggling KB assignment:', err)
    }
    setTogglingId(null)
  }

  const handleUploadFile = async () => {
    if (!uploadFile) return
    setUploading(true)
    try {
      // Upload to kb-docs bucket
      const fileName = `${Date.now()}-${uploadFile.name}`
      const { error: upErr } = await supabase.storage.from('kb-docs').upload(fileName, uploadFile)
      if (upErr) throw upErr

      // Try to read as text for now (Docling integration pending DOCLING_URL env var)
      const text = await uploadFile.text()
      setForm(f => ({
        ...f,
        contenido_md: text.slice(0, 10000),
        titulo: f.titulo || uploadFile.name.replace(/\.[^.]+$/, ''),
      }))
    } catch (err) {
      alert('Error al subir: ' + String(err))
    }
    setUploading(false)
  }

  /** Generate embedding for a single article via kb-embed edge function */
  const embedArticle = async (articleId: number) => {
    try {
      await supabase.functions.invoke('kb-embed', {
        body: { articulo_id: articleId },
      })
    } catch (err) {
      console.warn('Auto-embed failed for article', articleId, err)
    }
  }

  /** Batch-embed all articles missing embeddings */
  const handleBatchEmbed = async () => {
    setEmbedding(true)
    try {
      const { data, error } = await supabase.functions.invoke('kb-embed', {
        body: { batch: true },
      })
      if (error) throw error
      console.log('Batch embed result:', data)
      await loadArticles()
    } catch (err) {
      console.error('Batch embed failed:', err)
      alert('Error al generar embeddings: ' + String(err))
    }
    setEmbedding(false)
  }

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.contenido_md.trim()) return
    setSaving(true)

    const payload = {
      titulo: form.titulo.trim(),
      categoria: form.categoria,
      contenido_md: form.contenido_md,
      resumen: form.resumen.trim() || null,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      tokens_estimados: Math.ceil(form.contenido_md.length / 4),
      embedding: null, // Clear stale embedding — will be regenerated
      updated_at: new Date().toISOString(),
    }

    let savedId: number | null = null

    if (editingArticle) {
      await (supabase.from('kb_articulos') as any).update(payload).eq('id', editingArticle.id)
      savedId = editingArticle.id
    } else {
      const { data: inserted } = await (supabase.from('kb_articulos') as any)
        .insert({ ...payload, activo: true, orden: 0 })
        .select('id')
        .single()

      // Auto-assign new article to this agent
      if (inserted?.id) {
        savedId = inserted.id
        await (supabase.from('agente_kb_articulos') as any).insert({
          agente_id: agentId,
          articulo_id: inserted.id,
        })
      }
    }

    setSaving(false)
    setShowModal(false)
    loadArticles()

    // Auto-embed in background (non-blocking)
    if (savedId) {
      embedArticle(savedId).then(() => loadArticles())
    }
  }

  // Separate assigned and unassigned articles
  const assignedArticles = articles.filter(a => assignedIds.has(a.id))
  const unassignedArticles = articles.filter(a => !assignedIds.has(a.id))
  const totalTokens = assignedArticles.filter(a => a.activo).reduce((sum, a) => sum + (a.tokens_estimados || 0), 0)
  const activeAssigned = assignedArticles.filter(a => a.activo)
  const embeddedCount = activeAssigned.filter(a => embeddedIds.has(a.id)).length
  const missingEmbeddings = activeAssigned.length - embeddedCount
  const ragActive = embeddedCount > 0

  // Group articles by category
  const currentList = kbViewTab === 'assigned' ? assignedArticles : unassignedArticles
  const groupedArticles = currentList.reduce<Record<string, KbArticle[]>>((acc, a) => {
    const cat = a.categoria || 'general'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(a)
    return acc
  }, {})

  // Sort categories: pagina_web first, then alphabetical
  const sortedCategories = Object.keys(groupedArticles).sort((a, b) => {
    const order = ['pagina_web', 'empresa', 'servicios', 'faq', 'metodologia', 'casos_exito', 'precios', 'general']
    return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b))
  })

  const toggleGroup = (cat: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            {icons.book(18)} Knowledge Base
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {activeAssigned.length} asignados a {agentName} &middot; ~{totalTokens.toLocaleString()} tokens
            &middot; {embeddedCount}/{activeAssigned.length} con embedding
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {missingEmbeddings > 0 && (
            <button
              className="btn btn-secondary"
              onClick={handleBatchEmbed}
              disabled={embedding}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              {embedding ? 'Generando...' : `Generar Embeddings (${missingEmbeddings})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={openNew} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            + Nuevo Articulo
          </button>
        </div>
      </div>

      {/* RAG status indicator */}
      {ragActive ? (
        <div style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          background: 'color-mix(in srgb, var(--success) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
          fontSize: 13, color: 'var(--success)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {icons.check(14)} RAG activo — busqueda semantica con {embeddedCount} articulos. Solo los mas relevantes se inyectan por consulta (max 5).
          </span>
          {missingEmbeddings > 0 && (
            <span style={{ color: 'var(--warning)', fontSize: 12 }}>
              {icons.warning(12)} {missingEmbeddings} sin embedding (fallback secuencial)
            </span>
          )}
        </div>
      ) : (
        <div style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          background: 'color-mix(in srgb, var(--warning) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)',
          fontSize: 13, color: 'var(--warning)',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {icons.warning(14)} Sin embeddings — usando carga secuencial (limitado a ~4,000 chars). Genera embeddings para activar busqueda semantica RAG.
          </span>
        </div>
      )}

      {/* ── Tab switcher: Assigned / Unassigned ── */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 16,
        borderRadius: 10, overflow: 'hidden',
        border: '1px solid var(--border-color)',
      }}>
        {([
          { key: 'assigned' as const, label: `Asignados (${assignedArticles.length})` },
          { key: 'unassigned' as const, label: `No asignados (${unassignedArticles.length})` },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setKbViewTab(tab.key)}
            style={{
              flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
              background: kbViewTab === tab.key ? 'var(--color-primary)' : 'var(--bg-tertiary)',
              color: kbViewTab === tab.key ? 'var(--text-on-primary)' : 'var(--text-secondary)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Articles: grouped by category ── */}
      {loadingKb ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando articulos...</div>
      ) : currentList.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <p style={{ fontSize: 15, marginBottom: 8 }}>
            {kbViewTab === 'assigned'
              ? `No hay articulos asignados a ${agentName}`
              : 'Todos los articulos están asignados'}
          </p>
          <p style={{ fontSize: 13 }}>
            {kbViewTab === 'assigned'
              ? 'Sube PDFs o crea articulos manualmente para que el agente los use al responder.'
              : 'Cambia a la pestaña "Asignados" para administrar los articulos existentes.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedCategories.map(cat => {
            const catArticles = groupedArticles[cat]
            const catInfo = CATEGORY_LABELS[cat] || CATEGORY_LABELS.general
            const isCollapsed = collapsedGroups.has(cat)
            const catTokens = catArticles.reduce((sum, a) => sum + (a.tokens_estimados || 0), 0)
            const catEmbedded = catArticles.filter(a => embeddedIds.has(a.id)).length

            return (
              <div key={cat} style={{
                borderRadius: 10, overflow: 'hidden',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-surface)',
              }}>
                {/* Category header (clickable to collapse) */}
                <button
                  onClick={() => toggleGroup(cat)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', border: 'none', cursor: 'pointer',
                    background: `${catInfo.color}0A`,
                    borderBottom: isCollapsed ? 'none' : '1px solid var(--border-color)',
                    transition: 'background 0.2s',
                  }}
                >
                  {/* Collapse arrow */}
                  <span style={{
                    fontSize: 12, transition: 'transform 0.2s', display: 'inline-block',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    color: 'var(--text-secondary)',
                  }}>▼</span>

                  {/* Icon + label */}
                  <span style={{ fontSize: 15 }}>{catInfo.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                    {catInfo.label}
                  </span>

                  {/* Count badge */}
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                    background: `${catInfo.color}18`, color: catInfo.color,
                    border: `1px solid ${catInfo.color}30`,
                  }}>
                    {catArticles.length}
                  </span>

                  {/* Stats */}
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                    ~{catTokens.toLocaleString()} tokens
                    {kbViewTab === 'assigned' && ` · ${catEmbedded}/${catArticles.length} embeddings`}
                  </span>
                </button>

                {/* Articles in this category */}
                {!isCollapsed && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {catArticles.map(a => (
                      <ArticleRow
                        key={a.id}
                        article={a}
                        assigned={assignedIds.has(a.id)}
                        hasEmbedding={embeddedIds.has(a.id)}
                        toggling={togglingId === a.id}
                        onToggleActive={() => handleToggle(a)}
                        onToggleAssignment={() => toggleAssignment(a.id)}
                        onEdit={() => openEdit(a)}
                        onDelete={() => handleDelete(a)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Modal: Create/Edit Article ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {editingArticle ? <>{icons.edit(16)} Editar Articulo</> : <>{icons.fileText(16)} Nuevo Articulo</>}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>x</button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Upload dropzone */}
              {!editingArticle && (
                <div style={{
                  padding: 20, borderRadius: 12, marginBottom: 20,
                  border: '2px dashed var(--border)',
                  background: 'var(--bg-card-hover)',
                  textAlign: 'center',
                }}>
                  <div style={{ marginBottom: 8, opacity: 0.7 }}>{icons.upload(28)}</div>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                    Sube un PDF o DOCX para convertirlo a Markdown automaticamente
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      Seleccionar archivo
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc,.txt,.md"
                        onChange={e => setUploadFile(e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {uploadFile && (
                      <>
                        <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{uploadFile.name}</span>
                        <button className="btn btn-primary btn-sm" onClick={handleUploadFile} disabled={uploading}>
                          {uploading ? 'Procesando...' : 'Convertir'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Title + Category */}
              <div className="form-row" style={{ marginBottom: 18 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Titulo</label>
                  <input
                    className="form-input" value={form.titulo}
                    onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                    placeholder="Ej: Metodologia Agil 1Conecta"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Categoria</label>
                  <select
                    className="form-select" value={form.categoria}
                    onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Content */}
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Contenido (Markdown)</span>
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>
                    ~{Math.ceil(form.contenido_md.length / 4).toLocaleString()} tokens
                  </span>
                </label>
                <textarea
                  className="form-textarea"
                  value={form.contenido_md}
                  onChange={e => setForm(f => ({ ...f, contenido_md: e.target.value }))}
                  placeholder="Escribe o pega el contenido del articulo en Markdown..."
                  style={{ minHeight: 200, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 13, lineHeight: 1.6 }}
                />
              </div>

              {/* Summary */}
              <div className="form-group">
                <label className="form-label">
                  Resumen <span style={{ fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0 }}>(opcional -- se usa cuando el KB es muy grande)</span>
                </label>
                <textarea
                  className="form-textarea"
                  value={form.resumen}
                  onChange={e => setForm(f => ({ ...f, resumen: e.target.value }))}
                  placeholder="Resumen corto del articulo para optimizar tokens..."
                  style={{ minHeight: 60, fontSize: 13 }}
                />
              </div>

              {/* Tags */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Tags (separados por coma)</label>
                <input
                  className="form-input" value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="Ej: devops, cloud, aws"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button
                className="btn btn-primary" onClick={handleSave}
                disabled={saving || !form.titulo.trim() || !form.contenido_md.trim()}
              >
                {saving ? 'Guardando...' : editingArticle ? 'Actualizar' : 'Crear Articulo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Article Row Component ── */
interface ArticleRowProps {
  article: KbArticle
  assigned: boolean
  hasEmbedding: boolean
  toggling: boolean
  onToggleActive: () => void
  onToggleAssignment: () => void
  onEdit: () => void
  onDelete: () => void
}

function ArticleRow({ article, assigned, hasEmbedding, toggling, onToggleActive, onToggleAssignment, onEdit, onDelete }: ArticleRowProps) {
  const a = article
  const cat = CATEGORY_LABELS[a.categoria] || CATEGORY_LABELS.general
  const dimmed = !assigned

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: dimmed ? 'var(--bg-tertiary)' : (a.activo ? 'var(--bg-surface)' : 'var(--bg-tertiary)'),
      borderBottom: '1px solid var(--border-color)',
      opacity: dimmed ? 0.55 : (a.activo ? 1 : 0.6),
      transition: 'all 0.2s ease',
    }}>
      {/* Toggle active — only meaningful when assigned */}
      <button
        onClick={assigned ? onToggleActive : undefined}
        title={assigned ? (a.activo ? 'Desactivar' : 'Activar') : 'Asigna primero para activar'}
        style={{
          width: 36, height: 20, borderRadius: 10, border: 'none',
          cursor: assigned ? 'pointer' : 'default',
          background: assigned && a.activo ? 'var(--success)' : 'var(--bg-tertiary)',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
          outline: assigned ? undefined : '1px solid var(--border-color)',
        }}
      >
        <div style={{
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 2, left: assigned && a.activo ? 18 : 2,
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(var(--shadow-rgb), calc(.2 * var(--shadow-k)))',
        }} />
      </button>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{a.titulo}</span>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
            background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}30`,
          }}>
            {cat.label}
          </span>
          {a.archivo_nombre && (
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{icons.paperclip(12)} {a.archivo_nombre}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
          ~{(a.tokens_estimados || 0).toLocaleString()} tokens
          {a.tags.length > 0 && ` | ${a.tags.join(', ')}`}
          <span title={hasEmbedding ? 'Embedding generado (RAG activo)' : 'Sin embedding (fallback secuencial)'} style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: hasEmbedding ? 'var(--success)' : 'var(--warning)',
            flexShrink: 0,
          }} />
        </div>
      </div>

      {/* Assignment toggle */}
      <button
        className={`btn ${assigned ? 'btn-secondary' : 'btn-primary'}`}
        onClick={onToggleAssignment}
        disabled={toggling}
        style={{ padding: '4px 10px', fontSize: 11, flexShrink: 0 }}
      >
        {toggling ? '...' : assigned ? 'Desasignar' : 'Asignar'}
      </button>

      {/* Edit */}
      <button className="btn btn-secondary" onClick={onEdit} style={{ padding: '4px 10px', fontSize: 12 }}>
        Editar
      </button>
      {/* Delete */}
      <button className="btn btn-secondary" onClick={onDelete} style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)' }}>
        x
      </button>
    </div>
  )
}

/* ── Agent Memory Section ── */
interface AgentMemorySectionProps {
  agentId: number
  agentName: string
}

const MEMORY_LABELS: Record<TipoMemoria, { label: string; color: string }> = {
  preferencia: { label: 'Preferencia', color: 'var(--success)' },
  patron: { label: 'Patrón', color: 'var(--info)' },
  feedback: { label: 'Feedback', color: 'var(--danger)' },
  voz_marca: { label: 'Voz de Marca', color: 'var(--primary-light)' },
  aprendizaje: { label: 'Aprendizaje', color: 'var(--warning)' },
}

function AgentMemorySection({ agentId, agentName }: AgentMemorySectionProps) {
  const [memories, setMemories] = useState<AgenteMemoria[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ tipo: 'aprendizaje' as TipoMemoria, contenido: '' })
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState<string>('all')

  const loadMemories = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('agente_memoria')
        .select('*')
        .eq('agente_id', agentId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setMemories((data ?? []) as unknown as AgenteMemoria[])
    } catch (err) {
      console.error('Error loading agent memories:', err)
    } finally {
      setLoading(false)
    }
  }, [agentId])

  useEffect(() => {
    loadMemories()
  }, [loadMemories])

  const handleToggleActive = async (m: AgenteMemoria) => {
    try {
      const { error } = await (supabase.from('agente_memoria') as any)
        .update({ activo: !m.activo })
        .eq('id', m.id)

      if (error) throw error
      loadMemories()
    } catch (err) {
      console.error('Error toggling memory active state:', err)
    }
  }

  const handleDelete = async (m: AgenteMemoria) => {
    if (!confirm('¿Eliminar esta memoria de aprendizaje de forma permanente?')) return
    try {
      const { error } = await (supabase.from('agente_memoria') as any)
        .delete()
        .eq('id', m.id)

      if (error) throw error
      loadMemories()
    } catch (err) {
      console.error('Error deleting memory:', err)
    }
  }

  const handleSave = async () => {
    if (!form.contenido.trim()) return
    setSaving(true)
    try {
      // Invoke memory-extract for manual memory, which generates embedding and inserts
      const { data, error } = await supabase.functions.invoke('memory-extract', {
        body: {
          agent_id: agentId,
          manual_memory: {
            tipo: form.tipo,
            contenido: form.contenido.trim(),
          },
        },
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error)

      setForm({ tipo: 'aprendizaje', contenido: '' })
      setShowModal(false)
      loadMemories()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al guardar memoria')
    } finally {
      setSaving(false)
    }
  }

  const filteredMemories = memories.filter(m => filter === 'all' || m.tipo === filter)
  const lastLearned = memories.length > 0 ? memories[0].created_at : null

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            {icons.brain(18)} Memoria de Aprendizaje
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {memories.filter(m => m.activo).length} memorias activas de {agentName}
            {lastLearned && ` · Último aprendizaje: ${new Date(lastLearned).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Filter selector */}
          <select 
            className="form-select" 
            value={filter} 
            onChange={e => setFilter(e.target.value)}
            style={{ width: 'auto', padding: '6px 12px', fontSize: 13, height: 'auto', background: 'var(--bg-tertiary)' }}
          >
            <option value="all">Todas las memorias</option>
            {Object.entries(MEMORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            + Agregar Memoria
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando memorias...</div>
      ) : filteredMemories.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <p style={{ fontSize: 15, marginBottom: 8 }}>Sin memorias registradas</p>
          <p style={{ fontSize: 13 }}>
            {filter === 'all' 
              ? `A medida que converses con ${agentName}, irá extrayendo aprendizajes automáticamente de forma continua.`
              : `No se encontraron memorias activas para el filtro seleccionado.`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredMemories.map(m => {
            const cat = MEMORY_LABELS[m.tipo]
            return (
              <div 
                key={m.id} 
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                  background: m.activo ? 'var(--bg-surface)' : 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-color)',
                  opacity: m.activo ? 1 : 0.6,
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Toggle Active Switch */}
                <button
                  onClick={() => handleToggleActive(m)}
                  style={{
                    width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: m.activo ? 'var(--success)' : 'var(--border-color)',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2, left: m.activo ? 18 : 2,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(var(--shadow-rgb), calc(.2 * var(--shadow-k)))',
                  }} />
                </button>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                      {m.contenido}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                      background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}30`,
                      flexShrink: 0,
                    }}>
                      {cat.label}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>
                      Fuente: {m.fuente === 'conversacion' ? 'Conversación' : 'Manual'}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Registrada el {new Date(m.created_at).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>

                {/* Actions */}
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleDelete(m)} 
                  style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)', flexShrink: 0 }}
                >
                  x
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal: Add Manual Memory */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {icons.brain(16)} Agregar Memoria Manual
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>x</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Tipo de Memoria</label>
                <select
                  className="form-select"
                  value={form.tipo}
                  onChange={e => setForm(f => ({ ...f, tipo: e.target.value as TipoMemoria }))}
                >
                  {Object.entries(MEMORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Contenido / Aprendizaje</label>
                <textarea
                  className="form-textarea"
                  value={form.contenido}
                  onChange={e => setForm(f => ({ ...f, contenido: e.target.value }))}
                  placeholder="Ej: El cliente prefiere propuestas comerciales con presupuestos en Soles (PEN)."
                  rows={4}
                  style={{ minHeight: 100 }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave} 
                disabled={saving || !form.contenido.trim()}
              >
                {saving ? 'Procesando...' : 'Guardar Memoria'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
